import { ROOM_NOTIFY_SETTING_ENUM } from '_lib/constants/room';
import { blockUser, deleteFriendV2 } from '_api/friend';
import {
  NEW_OFFICIAL_MESSAGE_ID,
  RECENT_MESSAGE_ENTRY,
  RECENT_MESSAGE_SUB_ENTRY,
} from '@/common/constants/message';
import { calcUserName } from '_lib/CalcUserInfo';

import Vue from 'vue';
import EventBus from '_lib/EventBus';
import store from '@/store';

let deleteLock = false;

export function isSingleUser(channels) {
  if (!channels) {
    return false;
  }
  return (
    channels.length == 1 &&
    channels[0].friends.length == 1 &&
    channels[0].count <= 1
  );
}

export function clearUserInFriendsRoom(rooms, user_id) {
  for (let room_i in rooms) {
    let room = rooms[room_i];
    for (let channel_i in room.channels) {
      let channel = room.channels[channel_i];
      for (let user_i in channel.friends) {
        let user = channel.friends[user_i];
        if (user.user_id === user_id) {
          channel.friends.splice(user_i, 1);

          // 检查房间和频道是否是空的，如果空的也需要清理
          if (channel.friends.length === 0) {
            room.channels.splice(channel_i, 1);
          }
          if (room.channels.length === 0) {
            rooms.splice(room_i, 1);
          }

          return room;
        }
      }
    }
  }
}

/**
 * 删除好友统一工具函数
 * @param {*} user_id
 * @returns
 */
export function handleDeleteFriend(user_id, successCb) {
  if (deleteLock) return;
  deleteLock = true;

  successCb =
    successCb ??
    (() => {
      Vue.prototype.$toast.success('删除成功');
      EventBus.$emit('SOCKET_FRIENDS_STATE_UPDATE', {
        sequence: '',
        type: 20,
        data: {
          alt_type: 2,
          friends: [
            {
              user_id,
            },
          ],
          rooms: null,
        },
        timestamp: 0,
      });
    });

  Vue.prototype.$query({
    config: {
      title: '确定解除好友关系？',
      desc: '解除后你将看不到对方的实时语音状态',
      confirm: {
        text: '确定',
        type: 'alert',
      },
      cancel: {
        text: '取消',
      },
      neverShow: {
        text: '取消关注',
      },
    },
    confirmCB: ({ is_never_show: un_follow }) => {
      deleteFriendV2(
        {},
        {
          friend_id: user_id,
          un_follow: Number(un_follow),
        }
      )
        .then((res) => {
          if (res.data.status === 'ok') {
            successCb();
          }
        })
        .finally(() => {
          deleteLock = false;
        });
    },
  });
}

// 获取统一的消息处理菜单
export function getMessageUnreadHandlerItems(messageItem) {
  const { new_msg_count } = messageItem;
  const showSetUnread = new_msg_count > 0;

  const permissionArray = getEntryOperationPermissions(messageItem);

  let subGroups = [
    {
      hidden: showSetUnread
        ? !permissionArray.includes(getEntryOperationPermissionKey('unread'))
        : !permissionArray.includes(getEntryOperationPermissionKey('read')),
      component: 'menu-button',
      data: {
        label: showSetUnread ? '标为已读' : '标为未读',
      },
      onClick: async () => {
        EventBus.$emit('RecentMsg:Update_Unread', messageItem, showSetUnread);
      },
    },
    {
      hidden: !permissionArray.includes(getEntryOperationPermissionKey('hide')),
      component: 'menu-button',
      data: {
        label: '不显示',
      },
      onClick: () => {
        Vue.prototype.$query({
          config: {
            title: '不显示该聊天？',
            cancel: {
              text: '取消',
            },
            confirm: {
              text: '不显示',
            },
          },
          confirmCB: () => {
            EventBus.$emit('RecentMsg:Complete_Chat', messageItem);
          },
        });
      },
    },
    // 在消息列表里不显示该条会话框，同时删除聊天记录
    {
      hidden: !permissionArray.includes(
        getEntryOperationPermissionKey('delete')
      ),
      component: 'menu-button',
      data: {
        label: '删除',
        type: 'warn',
      },
      onClick: () => {
        Vue.prototype.$query({
          config: {
            title: '确认在当前列表删除该聊天并清空记录？',
            cancel: {
              text: '取消',
            },
            confirm: {
              text: '删除',
              type: 'alert',
            },
          },
          confirmCB: () => {
            EventBus.$emit('RecentMsg:Delete_Chat', messageItem);
          },
        });
      },
    },
  ];
  return subGroups;
}

/**
 * 获取最近消息的id
 * @param {*} messageData 来自talk/list接口的message数据
 * @returns
 */
export function getRecentMessageId(messageData) {
  if (messageData.entry === RECENT_MESSAGE_ENTRY.chat_group) {
    return messageData.chat_group_info.chat_group_id;
  } else {
    return messageData.user_id;
  }
}

/**
 * 锚定官方消息
 * @param {*} messageItem
 */
export function anchorOfficialMessage(messageItem) {
  const { entry, sub_entry } = messageItem;
  // 官方消息的entry
  const officialEntries = [
    RECENT_MESSAGE_ENTRY.heychat_sys_notify,
    RECENT_MESSAGE_ENTRY.heychat_interaction_msg,
    RECENT_MESSAGE_ENTRY.official_msg,
  ];
  // 官方消息的sub_entry
  const officialSubEntries = [RECENT_MESSAGE_SUB_ENTRY.friend_interaction];

  const isOfficialEntry = officialEntries.includes(entry);
  const isOfficialSubEntry =
    !sub_entry || officialSubEntries.includes(sub_entry);
  return isOfficialEntry && isOfficialSubEntry;
}

/**
 * 获取官方消息的id
 * @returns {Array<number>}
 */
export function getOfficialMessageIds() {
  const officialMessageIds = Array.from(Object.values(NEW_OFFICIAL_MESSAGE_ID));
  return officialMessageIds;
}

/**
 * 获取最近消息的计数差值
 * @param {*} messageItem
 * @returns
 */
export function getRecentMessageUnreadCountDiff(messageItem) {
  const { new_msg_count, entry, chat_group_info } = messageItem;
  const isChatGroup = entry === RECENT_MESSAGE_ENTRY.chat_group;
  if (isChatGroup) {
    const { chat_group_id } = chat_group_info;
    const groupNotifySetting =
      store.getters.all_chat_group_notify_settings[chat_group_id];
    if (groupNotifySetting === ROOM_NOTIFY_SETTING_ENUM.all) {
      return new_msg_count;
    } else {
      return 0;
    }
  } else {
    return new_msg_count;
  }
}

/**
 * 获取自增数
 * @param {*} messageItem
 */
export function getRecentMessageIncrementCount(messageItem) {
  const { entry, chat_group_info } = messageItem;
  const isChatGroup = entry === RECENT_MESSAGE_ENTRY.chat_group;
  if (isChatGroup) {
    const { chat_group_id } = chat_group_info;
    const groupNotifySetting =
      store.getters.all_chat_group_notify_settings[chat_group_id];
    if (groupNotifySetting === ROOM_NOTIFY_SETTING_ENUM.all) {
      return 1;
    } else {
      return 0;
    }
  } else {
    return 1;
  }
}

/**
 * 获取是否隐藏在线状态
 * @param {*} messageItem
 * @returns
 */
export function getShouldHideOnline(messageItem) {
  return messageItem.entry !== RECENT_MESSAGE_ENTRY.message;
}

/**
 * 获取消息入口权限
 * @param {*} messageItem 消息项
 * @returns {Array<string>}
 */
export function getEntryOperationPermissions(messageItem) {
  const { entry, sub_entry = '' } = messageItem;
  if (!entry) {
    console.error('[getEntryOperationPermissionByKey] entry is required');
    return [];
  }
  const matrix = store.getters.entry_can_operations;
  const permissionArray = matrix[entry]?.[sub_entry] || [];
  return permissionArray;
}

/**
 * 获取消息入口权限key
 * @param {*} key
 * @returns {string}
 */
export function getEntryOperationPermissionKey(key) {
  return `enable_${key}`;
}

/**
 * 处理用户加入或移出黑名单
 * @param {*} user
 */
export function handleChangeBlockUser(user) {
  const { user_id } = user;
  if (store.getters.blacklist.includes(user_id)) {
    blockUser(
      {},
      {
        to_user_id: user_id,
        add_to_blacklist: false,
      }
    ).then((res) => {
      if (res.data.status == 'ok') {
        let newBlackList = store.getters.blacklist.filter(
          (item) => item != user_id
        );
        store.commit('SET_BLACKLIST', newBlackList);
      }
    });
  } else {
    Vue.prototype.$query({
      config: {
        title: '将 ' + calcUserName(user) + ' 加入黑名单？',
        desc: '你将不再收到对方的消息',
        confirm: {
          text: '拉黑',
          type: 'alert',
        },
        cancel: {
          text: '取消',
        },
      },
      confirmCB: () => {
        blockUser(
          {},
          {
            to_user_id: user_id,
            add_to_blacklist: true,
          }
        ).then((res) => {
          if (res.data.status == 'ok') {
            let newBlackList = [user_id, ...store.getters.blacklist];
            store.commit('SET_BLACKLIST', newBlackList);
          }
        });
      },
    });
  }
}
